import { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '@/lib/api';
import {
  useSearchClients,
  useCreateClient,
  useCreateMoto,
  type ClientWithMotos,
} from '@/hooks/useClients';
import { useMechanics } from '@/hooks/useInterventions';
import { useAvailableSlots, type AvailableSlot } from '@/hooks/useSlots';
import { useDayInterventions } from '@/hooks/useDayInterventions';
import { useAtelierConfig } from '@/hooks/useAtelierConfig';
import { useHolidays, findHoliday } from '@/hooks/useHolidays';
import { useChassisSearch, type ChassisSearchMoto } from '@/hooks/useChassisSearch';
import { useNatures } from '@/hooks/useNatures';
import { useToast } from '@/hooks/useToast';
import WeekCalendar from './WeekCalendar';
import MecanoDayAgenda from './MecanoDayAgenda';
import {
  formatHeureMur,
  formatJourMur,
  todayIso,
  toIsoDate,
  addDays,
  fromIsoDate,
  mondayOfIso,
} from '@/lib/dates';
import type { Intervention, Motorcycle, TypeAgenda } from '@/lib/types';
import { TYPE_AGENDA_LABELS } from '@/lib/types';

type Props = {
  open: boolean;
  onClose: () => void;
  defaultTypeAgenda?: TypeAgenda;
  defaultDate?: string;
};

const DURATION_PRESETS_BY_TYPE: Record<TypeAgenda, number[]> = {
  rapide: [15, 30, 45, 60, 90, 120],
  mecanique: [60, 120, 180, 240, 360, 480],
  lavage: [15, 30, 45, 60],
};

export default function NewInterventionModal({ open, onClose, defaultTypeAgenda = 'rapide', defaultDate }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const naturesQuery = useNatures(open);
  const natureItems = useMemo(
    () => (naturesQuery.data ?? []).map((n) => n.libelle),
    [naturesQuery.data],
  );
  const [typeAgenda, setTypeAgenda] = useState<TypeAgenda>(defaultTypeAgenda);
  const [client, setClient] = useState<ClientWithMotos | null>(null);
  const [moto, setMoto] = useState<Motorcycle | null>(null);
  const [selectedNatures, setSelectedNatures] = useState<string[]>([]);
  const [autresNature, setAutresNature] = useState('');
  const [description, setDescription] = useState('');
  const [dureeEstimeeMinutes, setDuree] = useState(30);
  const [urgent, setUrgent] = useState(false);
  const [numeroServiceKtm, setNumeroServiceKtm] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [dateMin, setDateMin] = useState(defaultDate ?? todayIso());
  const [mecanoFilter, setMecanoFilter] = useState<string | undefined>(undefined);
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [weekStart, setWeekStart] = useState<string>(() => mondayOfIso(defaultDate ?? todayIso()));
  const [showNewClient, setShowNewClient] = useState(false);
  const [showPlateSearch, setShowPlateSearch] = useState(true);
  const skipMotoResetRef = useRef(false);
  const skipSlotResetRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTypeAgenda(defaultTypeAgenda);
    setClient(null);
    setMoto(null);
    setSelectedNatures([]);
    setAutresNature('');
    setDescription('');
    setDuree(typeAgenda === 'lavage' ? 15 : 30);
    setUrgent(false);
    setNumeroServiceKtm('');
    setSelectedSlot(null);
    setDateMin(defaultDate ?? todayIso());
    setWeekStart(mondayOfIso(defaultDate ?? todayIso()));
    setViewMode('day');
    setMecanoFilter(undefined);
    setShowNewClient(false);
    setShowPlateSearch(true);
    skipMotoResetRef.current = false;
    // Reset volontairement déclenché par l'ouverture seule (deps limitées à [open]).
  }, [open]);

  useEffect(() => {
    if (skipMotoResetRef.current) {
      skipMotoResetRef.current = false;
      return;
    }
    setMoto(null);
    setSelectedSlot(null);
  }, [client]);

  useEffect(() => {
    if (skipSlotResetRef.current) {
      skipSlotResetRef.current = false;
      return;
    }
    setSelectedSlot(null);
  }, [typeAgenda, dureeEstimeeMinutes, mecanoFilter, dateMin, weekStart, viewMode]);

  useEffect(() => {
    setSelectedNatures([]);
    setAutresNature('');
  }, [typeAgenda]);

  const nature = useMemo(
    () => [...selectedNatures, autresNature.trim()].filter(Boolean).join(', '),
    [selectedNatures, autresNature],
  );

  const mechanicsQuery = useMechanics();
  const mecanosForType = useMemo(
    () =>
      (mechanicsQuery.data ?? [])
        .filter((m) => m.specialite === typeAgenda && m.actif)
        .sort((a, b) => a.ordreAffichage - b.ordreAffichage),
    [mechanicsQuery.data, typeAgenda],
  );

  // Vue Jour : on cherche jusqu'à 7 jours suivants (le backend zappe les jours fermés/fériés).
  // Vue Semaine : on cible la semaine affichée (lundi → dimanche) avec maxResults=20 pour
  // récupérer un créneau/mécano/jour sur les 7 jours.
  const queryRange = useMemo(() => {
    if (viewMode === 'week') {
      const end = toIsoDate(addDays(fromIsoDate(weekStart), 6));
      return { dateMin: weekStart, dateMax: end, maxResults: 20 };
    }
    return {
      dateMin,
      dateMax: toIsoDate(addDays(fromIsoDate(dateMin), 7)),
      maxResults: undefined,
    };
  }, [viewMode, dateMin, weekStart]);

  const slotsQuery = useAvailableSlots(
    {
      typeAgenda,
      dureeEstimeeMinutes,
      mecanicienId: viewMode === 'week' ? mecanoFilter : undefined,
      dateMin: queryRange.dateMin,
      dateMax: queryRange.dateMax,
      maxResults: queryRange.maxResults,
    },
    open && !!client && !!moto && nature.length > 0,
  );

  const premierDispo = (slotsQuery.data?.length ?? 0) > 0 ? slotsQuery.data![0]! : null;

  // Vue Jour : un seul mécano affiché dans l'agenda (défaut = 1er éligible).
  const agendaMecanoId = mecanoFilter ?? mecanosForType[0]?.id;
  const agendaMecano = mecanosForType.find((m) => m.id === agendaMecanoId);

  const dayAgendaQuery = useDayInterventions(
    dateMin,
    agendaMecanoId,
    open && viewMode === 'day' && !!client && !!moto && nature.length > 0 && dureeEstimeeMinutes > 0,
  );

  // Config atelier (horaires + jours fermés) et fériés. Jours fermés = bloqués ;
  // fériés = signalés (couleur) mais réservables. heure-mur = UTC → getUTCDay.
  const configQuery = useAtelierConfig();
  const closedDays = configQuery.data?.closedDays ?? [0];
  const dayStartHour = configQuery.data?.dayStartHour ?? 9;
  const dayEndHour = configQuery.data?.dayEndHour ?? 18;
  const holidaysQuery = useHolidays(Number(dateMin.slice(0, 4)), open);
  const dayClosed = closedDays.includes(new Date(`${dateMin}T00:00:00Z`).getUTCDay());
  const dayHoliday = findHoliday(holidaysQuery.data, dateMin);

  function pickPremierDispo() {
    if (!premierDispo) return;
    skipSlotResetRef.current = true;
    setDateMin(premierDispo.debut.slice(0, 10));
    setMecanoFilter(premierDispo.mecanicienId);
    setSelectedSlot(premierDispo);
  }

  const createMutation = useMutation({
    mutationFn: () => {
      if (!client || !moto || !selectedSlot || !nature) {
        return Promise.reject(new Error('Champs obligatoires manquants'));
      }
      const dateRdv = selectedSlot.debut.slice(0, 10);
      return api.post<{ intervention: Intervention }>('/interventions', {
        clientId: client.id,
        motoId: moto.id,
        mecanicienId: selectedSlot.mecanicienId,
        typeAgenda,
        nature,
        description: description || undefined,
        dureeEstimeeMinutes,
        dateRdv,
        creneauDebut: selectedSlot.debut,
        urgent,
        kilometrageEntree: moto.kilometrage ?? undefined,
        numeroServiceKtm: numeroServiceKtm.trim() || undefined,
      });
    },
    onSuccess: () => {
      // Le créneau qu'on vient de réserver doit disparaître immédiatement des
      // créneaux libres (['slots']) et apparaître dans l'agenda du formulaire
      // (['day-interventions']), sans attendre les refetchInterval.
      void qc.invalidateQueries({ queryKey: ['interventions'] });
      void qc.invalidateQueries({ queryKey: ['slots'] });
      void qc.invalidateQueries({ queryKey: ['day-interventions'] });
      toast.success('RDV créé');
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof HttpError ? err.payload.message : 'Erreur lors de la création';
      toast.error(msg);
    },
  });

  function handleChassisPick(picked: ChassisSearchMoto) {
    skipMotoResetRef.current = true;
    setClient({
      id: picked.client.id,
      nom: picked.client.nom,
      prenom: picked.client.prenom,
      telephone: picked.client.telephone,
      email: picked.client.email ?? null,
      motos: [
        {
          id: picked.id,
          modele: picked.modele,
          vin: picked.vin,
          kilometrage: picked.kilometrage,
          annee: picked.annee,
        },
      ],
    });
    setMoto({
      id: picked.id,
      modele: picked.modele,
      vin: picked.vin,
      kilometrage: picked.kilometrage,
      annee: picked.annee,
    });
    setShowPlateSearch(false);
  }

  if (!open) return null;

  const canSubmit = client && moto && nature.length >= 2 && selectedSlot;

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-start justify-center pt-12 pb-12 px-4 z-40 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-surface-white rounded-lg shadow-lg border border-border-light w-full max-w-[720px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-border-light flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Nouveau rendez-vous</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary text-2xl leading-none"
            aria-label="Fermer"
          >
            ×
          </button>
        </header>

        <div className="flex-1 px-6 py-5 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Recherche rapide par plaque */}
          {!client && (
            <Section title="🔍 Recherche rapide par châssis">
              {showPlateSearch ? (
                <ChassisSearch
                  onPick={handleChassisPick}
                  onHide={() => setShowPlateSearch(false)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowPlateSearch(true)}
                  className="text-ktm-orange text-sm hover:underline"
                >
                  Rechercher une moto par châssis
                </button>
              )}
            </Section>
          )}

          {/* Type agenda */}
          <Section title="Type d'agenda">
            <div className="grid grid-cols-3 gap-3">
              {(['rapide', 'mecanique', 'lavage'] as TypeAgenda[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTypeAgenda(t);
                    setDuree(t === 'lavage' ? 15 : t === 'mecanique' ? 120 : 30);
                  }}
                  className={`px-3 py-3 rounded-md border text-sm font-medium transition-colors ${
                    typeAgenda === t
                      ? 'border-ktm-orange bg-ktm-orange-light text-ktm-orange'
                      : 'border-border-light bg-surface-white text-text-secondary hover:bg-surface-hover'
                  }`}
                >
                  {TYPE_AGENDA_LABELS[t]}
                </button>
              ))}
            </div>
          </Section>

          {/* Client */}
          <Section title="Client">
            {client ? (
              <SelectedClient client={client} onClear={() => setClient(null)} />
            ) : showNewClient ? (
              <InlineClientForm
                onCancel={() => setShowNewClient(false)}
                onCreated={(c) => {
                  setClient({ ...c, motos: [] });
                  setShowNewClient(false);
                }}
              />
            ) : (
              <ClientSearch onSelect={setClient} onCreateNew={() => setShowNewClient(true)} />
            )}
          </Section>

          {/* Moto */}
          {client && (
            <Section title="Moto">
              {moto ? (
                <SelectedMoto moto={moto} onClear={() => setMoto(null)} />
              ) : (
                <MotoSelector client={client} onSelect={setMoto} />
              )}
            </Section>
          )}

          {/* Détail intervention */}
          {client && moto && (
            <>
              <Section title="Détail intervention">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="input-label">Nature de l'intervention</label>
                    {typeAgenda !== 'lavage' &&
                      (naturesQuery.isLoading ? (
                        <div className="text-xs text-text-muted">Chargement des natures…</div>
                      ) : natureItems.length === 0 ? (
                        <div className="text-xs text-text-muted">
                          Aucune nature configurée — utilisez le champ libre ci-dessous.
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {natureItems.map((item) => {
                            const active = selectedNatures.includes(item);
                            return (
                              <button
                                key={item}
                                type="button"
                                onClick={() =>
                                  setSelectedNatures((prev) =>
                                    prev.includes(item)
                                      ? prev.filter((n) => n !== item)
                                      : [...prev, item],
                                  )
                                }
                                className={`px-2.5 py-1 rounded-sm text-xs font-medium border transition-colors ${
                                  active
                                    ? 'border-ktm-orange bg-ktm-orange-light text-ktm-orange'
                                    : 'border-border-light text-text-secondary hover:bg-surface-hover'
                                }`}
                              >
                                {item}
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    <input
                      id="autres-nature"
                      type="text"
                      value={autresNature}
                      onChange={(e) => setAutresNature(e.target.value)}
                      placeholder={
                        typeAgenda === 'lavage'
                          ? 'Type de lavage…'
                          : 'Autres (texte libre)'
                      }
                      className="input"
                    />
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="duree" className="input-label">
                      Durée estimée
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1 flex-wrap">
                        {DURATION_PRESETS_BY_TYPE[typeAgenda].map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setDuree(m)}
                            className={`px-2.5 py-1 rounded-sm text-xs font-medium border ${
                              dureeEstimeeMinutes === m
                                ? 'border-ktm-orange bg-ktm-orange-light text-ktm-orange'
                                : 'border-border-light text-text-secondary hover:bg-surface-hover'
                            }`}
                          >
                            {m < 60 ? `${m}m` : m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h${m % 60}`}
                          </button>
                        ))}
                      </div>
                      <input
                        id="duree"
                        type="number"
                        min={15}
                        step={15}
                        value={dureeEstimeeMinutes}
                        onChange={(e) => setDuree(Number(e.target.value))}
                        className="input w-24"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="description" className="input-label">
                      Description (optionnel)
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      placeholder="Précisions techniques, symptômes…"
                      className="input py-2 h-auto"
                    />
                  </div>

                  <div className="space-y-1">
                    <label htmlFor="numero-service-ktm" className="input-label">
                      N° de service KTM Cloud (optionnel)
                    </label>
                    <input
                      id="numero-service-ktm"
                      type="text"
                      value={numeroServiceKtm}
                      onChange={(e) => setNumeroServiceKtm(e.target.value)}
                      placeholder="Ex: SVC-2026-12345"
                      maxLength={30}
                      className="input font-mono"
                    />
                    <p className="text-xs text-text-muted">
                      Optionnel — peut être renseigné après le RDV via la fiche.
                    </p>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={urgent}
                      onChange={(e) => setUrgent(e.target.checked)}
                      className="w-4 h-4 accent-ktm-orange"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Marquer comme urgent</span>
                      <span className="text-text-muted"> · prioritaire dans la file</span>
                    </span>
                  </label>
                </div>
              </Section>

              {/* Créneau */}
              <Section title="Créneau proposé">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  {viewMode === 'day' && (
                    <DateShortcuts dateMin={dateMin} onChange={setDateMin} />
                  )}

                  {/* Toggle Vue Jour / Vue Semaine */}
                  <div
                    className="inline-flex border border-border-light rounded-md overflow-hidden ml-auto"
                    role="tablist"
                    aria-label="Mode d'affichage"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={viewMode === 'day'}
                      onClick={() => setViewMode('day')}
                      className={`px-3 h-8 text-xs font-medium transition-colors ${
                        viewMode === 'day'
                          ? 'bg-ktm-orange-light text-ktm-orange'
                          : 'bg-surface-white text-text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      Jour
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={viewMode === 'week'}
                      onClick={() => {
                        setViewMode('week');
                        setWeekStart(mondayOfIso(dateMin));
                      }}
                      className={`px-3 h-8 text-xs font-medium border-l border-border-light transition-colors ${
                        viewMode === 'week'
                          ? 'bg-ktm-orange-light text-ktm-orange'
                          : 'bg-surface-white text-text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      Semaine
                    </button>
                  </div>
                </div>

                {viewMode === 'day' && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {mecanosForType.map((m) => (
                      <MecanoChip
                        key={m.id}
                        active={agendaMecanoId === m.id}
                        couleur={m.couleur}
                        initial={m.prenom.charAt(0).toUpperCase()}
                        onClick={() => setMecanoFilter(m.id)}
                      >
                        {m.prenom}
                      </MecanoChip>
                    ))}
                  </div>
                )}

                {viewMode === 'week' ? (
                  <WeekCalendar
                    weekStart={weekStart}
                    onPrevWeek={() => setWeekStart(toIsoDate(addDays(fromIsoDate(weekStart), -7)))}
                    onNextWeek={() => setWeekStart(toIsoDate(addDays(fromIsoDate(weekStart), 7)))}
                    onTodayWeek={() => setWeekStart(mondayOfIso(todayIso()))}
                    slots={slotsQuery.data ?? []}
                    mecanos={mecanosForType}
                    selectedSlot={selectedSlot}
                    onPickSlot={setSelectedSlot}
                    closedDays={closedDays}
                    holidays={holidaysQuery.data}
                    isLoading={slotsQuery.isLoading}
                  />
                ) : (
                  <div className="space-y-3">
                    {premierDispo && (
                      <button
                        type="button"
                        onClick={pickPremierDispo}
                        className="w-full px-3 py-2 rounded-md border-2 border-ktm-orange-border bg-ktm-orange-light/40 hover:bg-ktm-orange-light text-sm flex items-center justify-between gap-3 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-base">⚡</span>
                          <span className="font-semibold text-ktm-orange uppercase tracking-wider text-xs">
                            Premier dispo
                          </span>
                          {(() => {
                            const m = mecanosForType.find((x) => x.id === premierDispo.mecanicienId);
                            return m ? (
                              <span className="text-text-primary font-medium">{m.prenom}</span>
                            ) : null;
                          })()}
                        </span>
                        <span className="font-mono text-text-secondary">
                          {formatJourMur(premierDispo.debut)} ·{' '}
                          <span className="text-text-primary font-semibold">
                            {formatHeureMur(premierDispo.debut)}
                          </span>
                        </span>
                      </button>
                    )}

                    {dayHoliday && !dayClosed && (
                      <div className="px-3 py-2 rounded-md bg-status-attente-pieces-bg text-status-attente-pieces-text text-xs font-medium flex items-center gap-2">
                        <span aria-hidden>📌</span>
                        <span>
                          Jour férié : {dayHoliday.nom} — l'atelier reste ouvert, réservation possible.
                        </span>
                      </div>
                    )}

                    {dayClosed ? (
                      <div className="px-4 py-6 rounded-md border border-border-light bg-surface-bg text-center">
                        <div className="text-sm font-semibold text-text-secondary">
                          Atelier fermé ce jour
                        </div>
                        <div className="text-xs text-text-muted mt-1">
                          Choisissez un autre jour{premierDispo ? ' ou utilisez ⚡ Premier dispo' : ''}.
                        </div>
                      </div>
                    ) : !agendaMecano ? (
                      <div className="text-sm text-text-muted">
                        Aucun mécanicien disponible pour ce type.
                      </div>
                    ) : (
                      <MecanoDayAgenda
                        mecano={agendaMecano}
                        dateIso={dateMin}
                        durationMin={dureeEstimeeMinutes}
                        interventions={dayAgendaQuery.data ?? []}
                        selectedSlot={selectedSlot}
                        onPickSlot={setSelectedSlot}
                        isLoading={dayAgendaQuery.isLoading}
                        dayStartHour={dayStartHour}
                        dayEndHour={dayEndHour}
                      />
                    )}
                  </div>
                )}
              </Section>
            </>
          )}

          {createMutation.isError && (
            <div className="px-4 py-3 rounded-md bg-status-annule-bg border border-status-annule-border text-status-annule-text text-sm">
              {createMutation.error instanceof HttpError
                ? createMutation.error.payload.message
                : 'Erreur lors de la création'}
            </div>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-border-light flex items-center justify-between gap-3">
          <div className="text-xs text-text-muted">
            {selectedSlot && (
              <>
                Livraison estimée :{' '}
                <span className="font-mono font-semibold text-text-primary">
                  {formatJourMur(selectedSlot.fin)} ·{' '}
                  {formatHeureMur(estimatedDelivery(typeAgenda, new Date(selectedSlot.fin), dureeEstimeeMinutes))}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Annuler
            </button>
            <button
              type="button"
              disabled={!canSubmit || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="btn btn-primary"
            >
              {createMutation.isPending ? 'Création…' : 'Créer le RDV'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function estimatedDelivery(type: TypeAgenda, fin: Date, durationMin: number): Date {
  if (type === 'mecanique' && durationMin >= 240) {
    const eod = new Date(fin);
    eod.setUTCHours(18, 0, 0, 0);
    return eod;
  }
  const buffer = type === 'rapide' ? 15 : type === 'mecanique' ? 30 : 5;
  return new Date(fin.getTime() + buffer * 60_000);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">{title}</h3>
      {children}
    </section>
  );
}

function ClientSearch({
  onSelect,
  onCreateNew,
}: {
  onSelect: (c: ClientWithMotos) => void;
  onCreateNew: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data: clients, isLoading } = useSearchClients(query);

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Rechercher par nom, prénom, téléphone…"
        className="input"
      />
      <div className="max-h-48 overflow-y-auto border border-border-light rounded-md divide-y divide-border-light">
        {isLoading ? (
          <div className="px-3 py-2 text-sm text-text-muted">Recherche…</div>
        ) : clients && clients.length > 0 ? (
          clients.slice(0, 10).map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-surface-hover flex items-center justify-between"
            >
              <span className="font-medium text-text-primary">
                {c.prenom} {c.nom}
              </span>
              <span className="font-mono text-xs text-text-muted">{c.telephone}</span>
            </button>
          ))
        ) : (
          <div className="px-3 py-2 text-sm text-text-muted">Aucun client</div>
        )}
      </div>
      <button
        type="button"
        onClick={onCreateNew}
        className="text-ktm-orange text-sm hover:underline"
      >
        + Créer un nouveau client
      </button>
    </div>
  );
}

function SelectedClient({
  client,
  onClear,
}: {
  client: ClientWithMotos;
  onClear: () => void;
}) {
  return (
    <div className="px-4 py-3 rounded-md border border-border-light bg-surface-hover flex items-center justify-between">
      <div>
        <div className="font-semibold text-text-primary">
          {client.prenom} {client.nom}
        </div>
        <div className="text-xs text-text-muted font-mono">{client.telephone}</div>
      </div>
      <button type="button" onClick={onClear} className="text-text-muted hover:text-text-primary text-sm">
        Changer
      </button>
    </div>
  );
}

function InlineClientForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (c: { id: string; nom: string; prenom: string; telephone: string; email: string | null }) => void;
}) {
  const createClient = useCreateClient();
  const [form, setForm] = useState({ prenom: '', nom: '', telephone: '+212' });

  async function submit() {
    try {
      const res = await createClient.mutateAsync({
        prenom: form.prenom,
        nom: form.nom,
        telephone: form.telephone,
      });
      onCreated({ ...res.client, email: res.client.email ?? null });
    } catch {
      // shown below
    }
  }

  return (
    <div className="space-y-3 border border-border-light rounded-md p-3 bg-surface-bg">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} />
        <Input label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} />
      </div>
      <Input
        label="Téléphone"
        value={form.telephone}
        onChange={(v) => setForm({ ...form, telephone: v })}
        placeholder="+212XXXXXXXXX"
      />
      {createClient.isError && (
        <div className="text-xs text-status-annule-text">
          {createClient.error instanceof HttpError
            ? createClient.error.payload.message
            : 'Erreur création'}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn btn-secondary btn-sm">
          Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={createClient.isPending || !form.prenom || !form.nom || !form.telephone}
          className="btn btn-primary btn-sm"
        >
          {createClient.isPending ? '…' : 'Créer'}
        </button>
      </div>
    </div>
  );
}

function MotoSelector({
  client,
  onSelect,
}: {
  client: ClientWithMotos;
  onSelect: (m: Motorcycle) => void;
}) {
  const [showNew, setShowNew] = useState(false);
  const createMoto = useCreateMoto();
  const [form, setForm] = useState({
    modele: '',
    vin: '',
    annee: '',
    kilometrage: '',
  });

  async function submitNew() {
    try {
      const res = await createMoto.mutateAsync({
        clientId: client.id,
        modele: form.modele,
        vin: form.vin,
        annee: form.annee ? Number(form.annee) : undefined,
        kilometrage: form.kilometrage ? Number(form.kilometrage) : undefined,
      });
      onSelect(res.moto);
    } catch {
      // shown below
    }
  }

  if (showNew) {
    return (
      <div className="space-y-3 border border-border-light rounded-md p-3 bg-surface-bg">
        <Input
          label="Modèle / Nom de la moto"
          value={form.modele}
          onChange={(v) => setForm({ ...form, modele: v })}
          placeholder="Ex: Duke 390, 1290 Super Adventure R…"
        />
        <Input
          label="Numéro de châssis (VIN) *"
          value={form.vin}
          onChange={(v) =>
            setForm({ ...form, vin: v.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '').slice(0, 17) })
          }
          placeholder="VBKEXA40HPC234567"
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Année (optionnel)"
            value={form.annee}
            onChange={(v) => setForm({ ...form, annee: v })}
            placeholder="2024"
          />
          <Input
            label="Kilométrage (optionnel)"
            value={form.kilometrage}
            onChange={(v) => setForm({ ...form, kilometrage: v })}
            placeholder="12500"
          />
        </div>
        {createMoto.isError && (
          <div className="text-xs text-status-annule-text">
            {createMoto.error instanceof HttpError
              ? createMoto.error.payload.message
              : 'Erreur création'}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => setShowNew(false)} className="btn btn-secondary btn-sm">
            Annuler
          </button>
          <button
            type="button"
            onClick={submitNew}
            disabled={createMoto.isPending || !form.modele || form.vin.length < 11}
            className="btn btn-primary btn-sm"
          >
            {createMoto.isPending ? '…' : 'Créer'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {client.motos.length > 0 ? (
        client.motos.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onSelect(m as Motorcycle)}
            className="w-full px-3 py-2 rounded-md border border-border-light bg-surface-white hover:bg-surface-hover text-left flex items-center justify-between"
          >
            <span className="font-medium text-text-primary">{m.modele}</span>
            <span className="text-xs text-text-muted font-mono">
              {m.vin}
              {m.kilometrage !== null ? ` · ${m.kilometrage.toLocaleString('fr-FR')} km` : ''}
            </span>
          </button>
        ))
      ) : (
        <div className="text-sm text-text-muted">Aucune moto enregistrée pour ce client.</div>
      )}
      <button type="button" onClick={() => setShowNew(true)} className="text-ktm-orange text-sm hover:underline">
        + Ajouter une moto
      </button>
    </div>
  );
}

function SelectedMoto({ moto, onClear }: { moto: Motorcycle; onClear: () => void }) {
  return (
    <div className="px-4 py-3 rounded-md border border-border-light bg-surface-hover flex items-center justify-between">
      <div>
        <div className="font-semibold text-text-primary">{moto.modele}</div>
        <div className="text-xs text-text-muted font-mono">
          {moto.vin}
          {moto.kilometrage !== null ? ` · ${moto.kilometrage.toLocaleString('fr-FR')} km` : ''}
        </div>
      </div>
      <button type="button" onClick={onClear} className="text-text-muted hover:text-text-primary text-sm">
        Changer
      </button>
    </div>
  );
}

function ChassisSearch({
  onPick,
  onHide,
}: {
  onPick: (m: ChassisSearchMoto) => void;
  onHide: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data: motos, isLoading } = useChassisSearch(query);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value.toUpperCase())}
          placeholder="Saisir 4-6 caractères du VIN..."
          className="input font-mono"
          autoFocus
        />
        <button
          type="button"
          onClick={onHide}
          className="text-text-muted hover:text-text-primary text-xs whitespace-nowrap"
        >
          Masquer
        </button>
      </div>
      {query.trim().length >= 2 && (
        <div className="max-h-48 overflow-y-auto border border-border-light rounded-md divide-y divide-border-light">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-text-muted">Recherche…</div>
          ) : motos && motos.length > 0 ? (
            motos.slice(0, 10).map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onPick(m)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-surface-hover flex items-center justify-between gap-3"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-text-primary truncate">{m.modele}</span>
                  <span className="font-mono text-xs text-text-muted whitespace-nowrap">
                    {m.vin}
                  </span>
                </span>
                <span className="text-xs text-text-secondary truncate">
                  {m.client.prenom} {m.client.nom}
                </span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-text-muted">Aucune moto trouvée</div>
          )}
        </div>
      )}
      {query.trim().length < 2 && (
        <p className="text-xs text-text-muted">
          Tapez au moins 2 caractères du châssis (raccourci pour les clients existants).
        </p>
      )}
    </div>
  );
}

function DateShortcuts({
  dateMin,
  onChange,
}: {
  dateMin: string;
  onChange: (iso: string) => void;
}) {
  const today = todayIso();
  const tomorrow = toIsoDate(addDays(fromIsoDate(today), 1));
  const isToday = dateMin === today;
  const isTomorrow = dateMin === tomorrow;
  const isOther = !isToday && !isTomorrow;

  const baseBtn =
    'h-8 px-3 text-xs font-medium border rounded-md transition-colors';
  const activeCls = 'border-ktm-orange bg-ktm-orange-light text-ktm-orange';
  const idleCls =
    'border-border-light bg-surface-white text-text-secondary hover:bg-surface-hover';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => onChange(today)}
        className={`${baseBtn} ${isToday ? activeCls : idleCls}`}
      >
        Aujourd'hui
      </button>
      <button
        type="button"
        onClick={() => onChange(tomorrow)}
        className={`${baseBtn} ${isTomorrow ? activeCls : idleCls}`}
      >
        Demain
      </button>
      <input
        type="date"
        value={dateMin}
        min={today}
        onChange={(e) => onChange(e.target.value)}
        className={`h-8 px-2 text-sm border rounded-md bg-surface-white focus:outline-none focus:border-ktm-orange ${
          isOther ? 'border-ktm-orange text-ktm-orange' : 'border-border-medium'
        }`}
      />
    </div>
  );
}

function MecanoChip({
  active,
  couleur,
  initial,
  onClick,
  children,
}: {
  active: boolean;
  couleur?: string;
  initial?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border transition-colors ${
        active
          ? 'border-ktm-orange bg-ktm-orange-light text-ktm-orange'
          : 'border-border-light text-text-secondary hover:bg-surface-hover'
      }`}
    >
      {couleur && initial && (
        <span
          className="w-4 h-4 rounded-sm flex items-center justify-center text-text-on-orange font-bold text-[10px]"
          style={{ backgroundColor: couleur }}
          aria-hidden
        >
          {initial}
        </span>
      )}
      {children}
    </button>
  );
}

function Input({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="input-label">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input"
      />
    </div>
  );
}
